import type { HTMLAttributes } from 'react';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import type { LanguageValue } from '@elfsight/configurator-contracts';
import { Show } from '@elfsight/lib-utils';
import type { BusinessHoursProps } from '@elfsight/widget-business-hours/common';
import { BusinessHours } from '@elfsight/widget-business-hours/widget';
import {
  ContactsAddressItem,
  ContactsEmailItem,
  ContactsPhoneItem,
  ContactsWebsiteItem,
  LocationLabels
} from '@elfsight/widget-location-card/widget';
import { Address, Badge, Typography } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { pick } from 'es-toolkit';
import type { StoreLocatorLocation } from '../../types';
import { useActiveLocation } from '../active-location-context';
import { Displayable, isDisplayable } from '../components/displayable';
import { LocationTags } from '../components/location-tags';
import { useMapAPI } from '../map-api-context';
import { formatDistance } from '../utils/format-distance';
import { LocationPreviewImageLayout } from './location-preview-image-layout';

export type LocationPreviewProps = HTMLAttributes<HTMLDivElement> & {
  location: StoreLocatorLocation & { distance?: number };
  withActions: boolean;
  hoverState?: boolean;
};

export const LocationPreview = forwardRef<HTMLDivElement, LocationPreviewProps>(
  ({ hoverState, location, withActions, ...forwardingProps }, ref) => {
    const {
      dayMonthFormat,
      timeFormat,
      firstWeekday,
      closedSoonStatusColor,
      closedStatusColor,
      openStatusColor,
      textColor,
      accentColor,
      previewCardHeadingStyle,
      previewCardTextStyle,
      previewCardElements,
      language,
      categories
    } = useSettings();
    const { t } = useLingui();
    const { activeLocation, setActiveLocation } = useActiveLocation();
    const { mapAPIRef } = useMapAPI();

    const {
      id,
      name,
      photo,
      distance,
      badge,
      email,
      website,
      address,
      phone,
      place,
      timeZone,
      categories: locationCategories
    } = location;

    const defaultLanguage: LanguageValue = {
      language: navigator.language ?? 'en',
      messages: {},
      label: ''
    };

    const businessHoursProps: BusinessHoursProps = {
      dayMonthFormat,
      timeFormat,
      timeZone,
      firstWeekday,
      closedSoonStatusColor,
      closedStatusColor,
      openStatusColor,
      textColor,
      language: language ?? defaultLanguage,
      textStyle: { fontWeight: 'bold', ...previewCardTextStyle },
      alignment: 'left',
      mode: withActions ? 'statusWithHours' : 'status',
      ...pick(location, [
        'specialIntervals',
        'daySundayHours',
        'daySundayOpen',
        'dayMondayHours',
        'dayMondayOpen',
        'dayTuesdayHours',
        'dayTuesdayOpen',
        'dayWednesdayHours',
        'dayWednesdayOpen',
        'dayThursdayHours',
        'dayThursdayOpen',
        'dayFridayHours',
        'dayFridayOpen',
        'daySaturdayHours',
        'daySaturdayOpen'
      ])
    };

    const contactsItemProps = {
      textColor,
      showLabel: false,
      showIcon: false,
      textStyle: previewCardTextStyle,
      lineHeight: 1.23,

      iconColor: textColor,
      withUnderline: false,
      underlineAvailable: withActions
    };

    const isPreviewDisplayable = isDisplayable(previewCardElements);

    const hasDistance = distance !== undefined;

    const locationLabels = locationCategories.map((id) => ({
      id,
      text: categories.find((category) => category.id === id)?.name
    }));

    return (
      <StyledLocationPreviewImageLayout
        ref={ref}
        _withAction={withActions}
        hoverState={hoverState || activeLocation?.id === id}
        onClick={(e) => {
          if (!withActions) {
            e.preventDefault();
          }
          setActiveLocation(id);
        }}
        onMouseOver={() => {
          mapAPIRef?.current?.setHoveredMarker(id);
        }}
        onMouseLeave={() => {
          mapAPIRef?.current?.setHoveredMarker(undefined);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            setActiveLocation(id);
          }
        }}
        image={photo}
        alt={name}
        {...forwardingProps}
      >
        {isPreviewDisplayable('name', name || badge) && (
          <Header>
            <Title
              lineHeight={1.5}
              textStyle={{
                fontWeight: 'bold',
                ...previewCardHeadingStyle
              }}
              color={textColor}
            >
              {name}
              <Show when={!!badge}>
                <>
                  &nbsp;
                  <Badge
                    color={accentColor}
                    size="medium"
                    style={{ marginBottom: -2 }}
                  >
                    {badge}
                  </Badge>
                </>
              </Show>
            </Title>
            <Show
              when={isPreviewDisplayable('categories', locationLabels.length)}
            >
              <LocationLabels labels={locationLabels} alignment="left" />
            </Show>
          </Header>
        )}

        <Metainfo
          displayCondition={hasDistance || isPreviewDisplayable('status')}
        >
          {hasDistance && (
            <Distance
              _textSize={previewCardTextStyle?.fontSize}
              _textColor={textColor}
            >
              {formatDistance(
                distance,
                language?.language ?? defaultLanguage.language
              )}
            </Distance>
          )}
          <Show when={hasDistance && isPreviewDisplayable('status')}>
            <Divider />
          </Show>
          <Show when={isPreviewDisplayable('status')}>
            <StyledBusinessHours
              {...businessHoursProps}
              _withDistance={hasDistance}
            />
          </Show>
        </Metainfo>

        <Block
          displayCondition={
            isPreviewDisplayable('address', address) ||
            isPreviewDisplayable('phone', phone) ||
            isPreviewDisplayable('email', email) ||
            isPreviewDisplayable('website', website)
          }
        >
          <Show when={isPreviewDisplayable('address', address)}>
            <ContactsAddressItem
              value={
                <Address
                  phrases={{
                    open: t`Open in Google Maps`,
                    getDirection: t`Get Directions`,
                    copy: t`Copy`
                  }}
                  fontSize={previewCardTextStyle?.fontSize}
                  color={textColor}
                  address={address}
                  withAction={withActions}
                  locationAddress={place?.address}
                />
              }
              {...contactsItemProps}
            />
          </Show>

          <Show when={isPreviewDisplayable('phone', phone)}>
            <ContactsPhoneItem {...contactsItemProps} value={phone} />
          </Show>
          <Show when={isPreviewDisplayable('email', email)}>
            <ContactsEmailItem {...contactsItemProps} value={email} />
          </Show>

          <Show when={isPreviewDisplayable('website', website)}>
            <ContactsWebsiteItem {...contactsItemProps} value={website} />
          </Show>
        </Block>

        <Show when={isPreviewDisplayable('tags', location.tags.length)}>
          <Tags tags={location.tags} as={LocationTags} />
        </Show>
      </StyledLocationPreviewImageLayout>
    );
  }
);

const Header = styled.div.attrs({
  className: createReadableClass({
    component: 'location-preview',
    element: 'header'
  })
})`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled(Typography).attrs({
  className: createReadableClass({
    component: 'location-preview',
    element: 'title'
  })
})``;

const Metainfo = styled(Displayable).attrs({
  className: createReadableClass({
    component: 'location-preview',
    element: 'metainfo'
  })
})`
  display: inline-block;
`;

const Divider = styled.div.attrs({
  className: createReadableClass({
    component: 'location-preview',
    element: 'divider'
  })
})`
  height: 4px;
  width: 4px;
  border-radius: 50%;
  background: #111;
  opacity: 50%;
  margin: 0 8px;
  display: inline-block;
  transform: translateY(-50%);
`;

const Distance = styled.div.attrs({
  className: createReadableClass({
    component: 'location-preview',
    element: 'distance'
  })
})<{
  _textSize: number;
  _textColor: string;
}>`
  font-weight: 700;
  display: inline;
  font-size: ${({ _textSize }) => _textSize}px;
  color: ${({ _textColor }) => _textColor};
`;

const StyledBusinessHours = styled(BusinessHours)<{
  _withDistance?: boolean;
}>`
  ${({ _withDistance }) => _withDistance && 'display: inline;'}
`;

const StyledLocationPreviewImageLayout = styled(
  LocationPreviewImageLayout
).attrs({
  className: createReadableClass({
    component: 'location-preview',
    element: 'image'
  })
})<{
  _withAction?: boolean;
}>`
  ${({ _withAction }) =>
    _withAction
      ? css`
          cursor: default;
        `
      : css`
          cursor: pointer;
        `};
`;

const Block = styled(Displayable).attrs({
  className: createReadableClass({
    component: 'location-preview',
    element: 'block'
  })
})`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Tags = styled(LocationTags)`
  margin-top: 8px;
`;
